import { createContext, useContext, useEffect, useMemo } from 'react'
import { usePage } from '@inertiajs/react'
import { CATALOGS, resolveCatalog } from './locales'

// Client UI translation, SSR-safe. The server resolves the visitor's locale from
// Accept-Language (LocaleResolver) and shares it as the `locale` prop, so the SSR
// render and the first client render agree — pages render in-language on the
// server (correct SEO, no flash) with no hydration mismatch. Missing keys fall
// back to English, then to the key itself. Mirrors the app's other client
// providers (UnitsProvider / StationsProvider).

const I18nContext = createContext({ t: (k) => k, locale: 'en' })

export function interpolate(str, vars) {
  if (!vars || typeof str !== 'string') return str
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{{${k}}}`))
}

export function LocaleProvider({ children }) {
  const locale = usePage().props.locale || 'en'

  // Reflect the locale on <html lang> for a11y/SEO (the server already sets it;
  // this keeps it correct across SPA navigations).
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale
  }, [locale])

  const t = useMemo(() => {
    const en = CATALOGS.en
    const cat = CATALOGS[locale] || CATALOGS[resolveCatalog(locale)] || en
    return (key, vars) => {
      const val = cat[key] != null ? cat[key] : en[key] != null ? en[key] : key
      return interpolate(val, vars)
    }
  }, [locale])

  const value = useMemo(() => ({ t, locale }), [t, locale])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => useContext(I18nContext)
export const useT = () => useContext(I18nContext).t
